import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/** HeaderDrawer: native <details> drawer with accordion + overlay */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('toggle', this.#onToggle, { capture: true });
    this.addEventListener('click', this.#onLinkClick, { capture: true });
    this.addEventListener('click', this.#onBurgerClick, { capture: true });
    this.#setupAnimatedElementListeners();
    this.#ensureSecondaryFooter();
    this.#setupOverlay();
  }

  #onBurgerClick = (e) => {
  const trigger = e.target?.closest?.('.header__icon--menu');
  if (!trigger || !this.contains(trigger)) return;
  e.preventDefault();
  this.open();
};

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#onToggle, { capture: true });
    this.removeEventListener('click', this.#onLinkClick, { capture: true });
  }

  get isOpen() { return this.refs.details.hasAttribute('open'); }
  toggle() { return this.isOpen ? this.close() : this.open(); }
  open() { if (!this.isOpen) this.refs.details.setAttribute('open',''); }
  back() {
    const openSub = this.querySelector('details[open]:not(.menu-drawer)');
    openSub ? this.#closeDetails(openSub) : this.close();
  }
  close() { this.#closeDetails(this.refs.details); }

  #onKeyUp = (e) => {
    if (e.key !== 'Escape') return;
    const target = e.target instanceof Element ? e.target : this;
    const openAncestor = target.closest?.('details[open]') || this.refs.details;
    this.#closeDetails(openAncestor);
  };

  #onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !this.contains(details)) return;
    const summary = details.querySelector('summary');

    if (details.hasAttribute('open')) {
      if (!details.classList.contains('menu-drawer')) this.#closeOtherSubmenus(details);
      details.classList.add('menu-open');
      summary?.setAttribute('aria-expanded','true');

      if (details === this.refs.details) {
        document.body.classList.add('drawer-open');
        document.body.style.overflow = 'hidden';
        setTimeout(() => trapFocus(this.refs.details), 0);
        this.#showOverlay();
      }
    } else {
      summary?.setAttribute('aria-expanded','false');
      details.classList.remove('menu-open');
      if (details === this.refs.details) {
        document.body.classList.remove('drawer-open');
        document.body.style.overflow = '';
        this.#hideOverlay();
        onAnimationEnd(details, () => {
          removeTrapFocus();
          this.querySelectorAll('details[open]:not(.menu-drawer)').forEach(this.#resetDetails);
        });
      }
    }
  };

  #onLinkClick = (e) => {
    const link = e.target?.closest?.('a');
    if (!link || !this.contains(link)) return;
    e.stopPropagation();
    if (link.href && !link.href.includes('#')) setTimeout(() => this.close(), 150);
  };

  #closeDetails(d) { if (d instanceof HTMLDetailsElement && d.hasAttribute('open')) d.removeAttribute('open'); }
  #resetDetails = (d) => { d.classList.remove('menu-open'); d.removeAttribute('open'); d.querySelector('summary')?.setAttribute('aria-expanded','false'); };
  #closeOtherSubmenus(current) {
    this.querySelectorAll('details[open]:not(.menu-drawer)').forEach((d) => {
      if (d === current) return;
      if (d.contains(current) || current.contains(d)) return;
      this.#resetDetails(d);
    });
  }
  #setupAnimatedElementListeners() {
    this.querySelectorAll('.menu-drawer__animated-element').forEach((el)=>{
      function clear(e){ const n=e.target; if(n instanceof HTMLElement){ n.style.setProperty('will-change','unset'); n.removeEventListener('animationend',clear); } }
      el.addEventListener('animationend', clear);
    });
  }
  #setupOverlay() {
    const ov = this.querySelector('.menu-drawer__overlay');
    if (ov) ov.addEventListener('click', () => this.close());
  }
  #showOverlay() {
    const ov = this.querySelector('.menu-drawer__overlay');
    if (!ov) return;
    ov.style.display = 'block'; ov.offsetHeight; ov.style.opacity = '1'; ov.style.visibility = 'visible';
  }
  #hideOverlay() {
    const ov = this.querySelector('.menu-drawer__overlay');
    if (!ov) return;
    ov.style.opacity = '0'; ov.style.visibility = 'hidden'; setTimeout(()=>{ ov.style.display='none'; }, 300);
  }
  #ensureSecondaryFooter() {
    const content = this.querySelector('.menu-drawer__content');
    const footer  = this.querySelector('.menu-drawer__footer');
    if (!content || !footer) return;
    content.style.display = 'flex';
    content.style.flexDirection = 'column';
    content.style.minHeight = '100vh';
    if (!content.contains(footer) || content.lastElementChild !== footer) content.appendChild(footer);
    footer.style.marginTop = 'auto';
  }
}

if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);
export default HeaderDrawer;
